import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import React from 'react'
import { useNegativeFeedbackConversation } from '../../hooks/analytics/useAnalytics'
import { ConversationMessage } from '../../types/analyticsTypes'
import MarkdownContent from '../MarkdownContent'
import ReferencesDisplay from '../Answer/ReferencesDisplay'

interface NegativeFeedbackDrawerProps {
  messageId: string | null
  isOpen: boolean
  onClose: () => void
}

const DownvoteFeedback: React.FC<{ message: ConversationMessage }> = ({ message }) => {
  if (!message.reason_bad_response && !message.feedback_bad_response) return null
  return (
    <Box mt={3} pt={3} borderTopWidth='1px' borderColor='app.border'>
      {message.reason_bad_response && (
        <Badge colorScheme='red' mr={2}>
          {message.reason_bad_response}
        </Badge>
      )}
      {message.feedback_bad_response && (
        <Text fontSize='xs' color='app.text.secondary' mt={1}>
          {message.feedback_bad_response}
        </Text>
      )}
    </Box>
  )
}

const MessageBubble: React.FC<{ message: ConversationMessage }> = ({ message }) => {
  // User questions keep a subtle right-aligned bubble; bot answers render like
  // the general chat — no bubble, full width — so they read the same way here.
  if (message.role === 'USER') {
    return (
      <Flex justify='flex-end'>
        <Box maxW='85%' bg='app.bg.subtle' borderRadius='2xl' px={4} py={3}>
          <MarkdownContent content={message.content} fontSize='sm' />
        </Box>
      </Flex>
    )
  }

  return (
    <Box width='100%'>
      <MarkdownContent content={message.content} references={message.sources} fontSize='sm' />
      {message.sources.length > 0 && (
        <Box mt={3}>
          <ReferencesDisplay references={message.sources} answerText={message.content} />
        </Box>
      )}
      {message.is_bad_response && <DownvoteFeedback message={message} />}
    </Box>
  )
}

const NegativeFeedbackDrawer: React.FC<NegativeFeedbackDrawerProps> = ({
  messageId,
  isOpen,
  onClose,
}) => {
  const { data: messages, isLoading, isError } = useNegativeFeedbackConversation(messageId)

  return (
    <Drawer isOpen={isOpen} placement='right' onClose={onClose} size='md'>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth='1px'>Conversation context</DrawerHeader>
        <DrawerBody>
          {isLoading && (
            <Flex justify='center' py={10}>
              <Spinner />
            </Flex>
          )}
          {isError && <Text color='red.500'>Could not load the conversation.</Text>}
          {!isLoading && !isError && (
            <VStack align='stretch' spacing={4} py={2}>
              {(messages ?? []).map(message => (
                <MessageBubble key={message.id} message={message} />
              ))}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default NegativeFeedbackDrawer
